import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { registerApplicant, uploadResume } from '../api';

const inputClass =
  'w-full rounded-lg border border-leather bg-leather/30 px-3 py-2 text-white placeholder-station-muted focus:ring-2 focus:ring-gold focus:border-gold';
const labelClass = 'block text-sm font-medium text-gold/90 mb-1';

export default function RegistrationPage() {
  const [name, setName] = useState('');
  const [industry, setIndustry] = useState('');
  const [grossProduction, setGrossProduction] = useState<number | ''>('');
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!name.trim() || !industry.trim() || grossProduction === '' || Number(grossProduction) < 0) return;
    setSubmitting(true);
    try {
      let resume_r2_key: string | undefined;
      if (resumeFile) {
        const { key } = await uploadResume(resumeFile);
        resume_r2_key = key;
      }
      await registerApplicant({
        name: name.trim(),
        industry: industry.trim(),
        gross_production: Number(grossProduction),
        linkedin_url: linkedinUrl.trim() || undefined,
        resume_r2_key,
      });
      setSuccess(true);
      setName('');
      setIndustry('');
      setGrossProduction('');
      setLinkedinUrl('');
      setResumeFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-charcoal">
      <header className="bg-charcoal text-gold border-b-2 border-gold/40">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="text-xl font-semibold text-gold tracking-tight hover:text-gold/80 transition-colors">
            The Station
          </Link>
          <span className="text-sm text-gold/90 font-medium">Partner Registration</span>
        </div>
      </header>
      <main className="flex-1 max-w-xl w-full mx-auto px-4 py-8">
        <div className="rounded-xl border border-leather bg-leather/40 p-6">
          <h1 className="text-2xl font-semibold text-gold mb-2">Apply to become a partner</h1>
          <p className="text-station-muted text-sm mb-6">
            Submit your details for screening. We&apos;ll review and get back to you.
          </p>
          {success && (
            <div className="mb-4 p-4 rounded-lg bg-gold/20 border border-gold/50 text-gold">
              Thank you. Your application has been received and is pending review.
            </div>
          )}
          {error && (
            <div className="mb-4 p-4 rounded-lg bg-red-500/20 border border-red-400/50 text-red-300 text-sm">
              {error}
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className={labelClass}>Full name</label>
              <input
                type="text"
                className={inputClass}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                required
              />
            </div>
            <div>
              <label className={labelClass}>Industry</label>
              <input
                type="text"
                className={inputClass}
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                placeholder="e.g. Restaurant, Retail"
                required
              />
            </div>
            <div>
              <label className={labelClass}>Gross production (annual $)</label>
              <input
                type="number"
                min={0}
                step={1000}
                className={inputClass}
                value={grossProduction === '' ? '' : grossProduction}
                onChange={(e) => setGrossProduction(e.target.value === '' ? '' : Number(e.target.value))}
                placeholder="0"
                required
              />
            </div>
            <div>
              <label className={labelClass}>LinkedIn profile (optional)</label>
              <input
                type="url"
                className={inputClass}
                value={linkedinUrl}
                onChange={(e) => setLinkedinUrl(e.target.value)}
                placeholder="https://linkedin.com/in/..."
              />
            </div>
            <div>
              <label className={labelClass}>Resume (optional)</label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.doc,.docx"
                className="block w-full text-sm text-station-muted file:mr-4 file:rounded file:border-0 file:bg-gold file:px-3 file:py-2 file:text-charcoal file:font-medium hover:file:bg-gold/90"
                onChange={(e) => setResumeFile(e.target.files?.[0] ?? null)}
              />
              {resumeFile && <p className="mt-1 text-xs text-gold/80">{resumeFile.name}</p>}
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-lg bg-gold text-charcoal font-medium py-2.5 hover:bg-gold/90 transition-colors disabled:opacity-50"
            >
              {submitting ? 'Submitting…' : 'Submit application'}
            </button>
          </form>
          <p className="mt-4 text-center">
            <Link to="/" className="text-gold/80 hover:text-gold text-sm">
              ← Back to Performance Portal
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
}
